import { ArrowDropDown, ArrowRight } from '@mui/icons-material'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import { Box, Card, IconButton, Stack, styled, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Flex } from 'src/styles/flexComponent'
import { GetRGBA } from 'src/utils'
import { AppRoutesNavigation } from '../../app-routes'
import { ReactComponent as SettingsIcon } from '../../assets/svg/settings.svg'
import { ReactComponent as VesselIcon } from '../../assets/svg/vessel.svg'
import { useDialogCtx } from '../../hooks/context-hooks'
import GlobalCalendarDialog from '../../screens/customer/calendar/GlobalCalendarDialog'
import { GridActionButton } from '../common/GridActionButton'
import VesselTasksCalendarDialog from '../locality/VesselTasksCalendarDialog'
import CustomerLocalitiesStatus from './CustomerLocalitiesStatus'
import UnifiedReportButton from './UnifiedReportButton'

interface CustomerHeaderProps {
  id: number
  name: string
  image: string
  active?: boolean
  onClick?: (e: any) => void
  MultipleSelectComponent?: React.ReactNode
  hasAdminAccess?: boolean
  isServiceStation?: boolean
  isProject?: boolean
  customerActivitySummaryData?: any
  isLandingPage?: boolean
  isLoading?: boolean
  subComponentEnable?: boolean
  isGlobalServiceStationProvider: boolean
  imageWidth?: string
  isAdminPortal?: boolean
  encryptedPermission?: string
  hasClientAdminPortalAccess?: boolean
}

interface ShadowedCardProps {
  changeColor?: boolean
  padding?: string
  isLandingPage?: boolean
  isAdminPortal?: boolean
  height?: string
  isProject?: boolean
}

export interface CommonInterface {
  isLandingPage?: boolean
  isServiceStationSmallHeader?: boolean
  img?: any
  imageWidth?: string
  height?: string
  minWidth?: string
  fontSize?: string
}

interface CustomerActionRowProps {
  customerId: EntityId
  active?: boolean
  isLandingPage?: boolean
  isServiceStationSmallHeader?: boolean
  isGlobalServiceProvider: boolean
  isProject?: boolean
}

export const CustomerHeader = ({
  id,
  name,
  image,
  active,
  onClick,
  isLandingPage,
  isServiceStation = false,
  isAdminPortal = false,
  MultipleSelectComponent,
  hasAdminAccess,
  customerActivitySummaryData,
  isLoading,
  subComponentEnable,
  isGlobalServiceStationProvider,
  imageWidth = '75px',
  encryptedPermission,
  hasClientAdminPortalAccess,
  isProject,

}: CustomerHeaderProps) => {
  const theme = useTheme()
  const isMediumScreen = useMediaQuery(theme.breakpoints.down(1270)) && isLandingPage
  const isTitleInMediumScreen = useMediaQuery(theme.breakpoints.down(1450))
  const isMobile = useMediaQuery(theme.breakpoints.down(600))
  const isLogoHidden = useMediaQuery(theme.breakpoints.down(1610))
  return (
    <ShadowedCard
      padding="0px 8px 0px 0px"
      isLandingPage={isLandingPage}
      isAdminPortal={isAdminPortal}
      isProject={isProject}
      height={!isLandingPage && isProject ? '44px' : undefined}
    >
      <Flex.Row
        alignItems="center"
        justifyContent={'space-between'}
        padding={0}
        height={!isLandingPage ? (isProject ? '44px' : '64px') : 'inherit'}
      >
        <Flex.Row
          alignItems="center"
          onClick={onClick}
          height="52px"
          padding={0}
          margin={0}
          width={isLandingPage ? 'auto' : '107px'}
        >
          {isLandingPage && <Chevron>{active ? <ArrowDropDown /> : <ArrowRight />}</Chevron>}
          {(!isLogoHidden || !isLandingPage) && (
            <>
              {image != null && image != undefined && image != '' ? (
                <CustomerLogo imageWidth={imageWidth} isLandingPage={isLandingPage} img={image} />
              ) : (
                <StyledEmptyLogoBox isLandingPage={isLandingPage}> {getNameLettersForLogo(name)} </StyledEmptyLogoBox>
              )}
            </>
          )}
          <StyledContainer isLandingPage={isLandingPage}>
            <Box
              width={
                isLandingPage
                  ? isMobile
                    ? '300px'
                    : customerActivitySummaryData
                    ? isTitleInMediumScreen
                      ? '300px'
                      : '400px'
                    : '700px'
                  : '180px'
              }
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <AutoScaleName
                text={name}
                islandingPage={isLandingPage}
                hasActivityData={!!customerActivitySummaryData}
              />
            </Box>
            {!isServiceStation && !isMediumScreen && customerActivitySummaryData && (
              <CustomerLocalitiesStatus
                customerActivitySummaryData={customerActivitySummaryData}
                isLandingPage={isLandingPage}
                loading={isLoading}
              />
            )}
            {subComponentEnable && <>{MultipleSelectComponent}</>}
          </StyledContainer>
        </Flex.Row>
        <Flex.Row
          alignItems="center"
          margin={`${isLandingPage || isServiceStation || isProject ? '0px' : '11px 3px 37px 0px'}`}
        >
          {isLandingPage && (
            <Box
              sx={{
                ml: 'auto',
                paddingRight: '8px',
                height: '44px',
                width: '173px',
                [theme.breakpoints.down(1800)]: { width: 66 },
              }}
            >
              {active && MultipleSelectComponent}
            </Box>
          )}
          {hasAdminAccess && !isServiceStation && !isProject && (
            <Box sx={{ ml: 1, display: 'flex', gap: 1 }}>
              {isLandingPage && <UnifiedReportButton customerId={id} customerName={name.trim()} />}
              {isLandingPage && <CalendarButton customerId={id} name={name} />}
              {isLandingPage && <VesselTasksButton customerId={id} encryptedPermission={encryptedPermission ?? ''} />}
            </Box>
          )}
        </Flex.Row>
      </Flex.Row>
    </ShadowedCard>
  )
}
interface AutoScaleProps {
  text: string
  islandingPage?: boolean
  hasActivityData?: boolean
}

export const AutoScaleName = ({ text, islandingPage, hasActivityData }: AutoScaleProps) => {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const maxFont = !islandingPage ? 18 : 21
    const minFont = islandingPage ? 18 : 14
    let size = maxFont

    el.style.fontSize = `${size}px`
    el.style.textOverflow = 'ellipsis'

    const containerWidth = el.parentElement?.clientWidth || (islandingPage ? 400 : 180)

    while (el.scrollWidth > containerWidth && size > minFont) {
      size -= 0.5
      el.style.fontSize = `${size}px`
    }

    if (el.scrollWidth > containerWidth) {
      el.style.textOverflow = 'ellipsis'
    }
  }, [text, hasActivityData])

  return (
    <Tooltip title={text}>
      <Name ref={ref} isLandingPage={islandingPage}>
        <span style={{ textTransform: 'capitalize' }}>
        {text}
        </span>
      </Name>
    </Tooltip>
  )
}

const CustomerActionRow = ({
  customerId,
  isLandingPage,
  isServiceStationSmallHeader,
  isGlobalServiceProvider,
  isProject,
}: CustomerActionRowProps) => {
  const navigate = useNavigate()
  const theme = useTheme()

  return (
    <StyledCustomerActionRow
      isLandingPage={isLandingPage}
      isServiceStationSmallHeader={isServiceStationSmallHeader}
      theme={theme}
    >
      <IconButton
        onClick={e => {
          e.stopPropagation()
          localStorage.setItem('selectedCustomerAdminCustomerId', customerId.toString())
          if (isGlobalServiceProvider) {
            navigate(AppRoutesNavigation.Customer.serviceStations(customerId))
          } else if (isProject) {
            navigate(AppRoutesNavigation.Customer.projects(customerId))
          } else {
            navigate(AppRoutesNavigation.Customer.localities(customerId))
          }
        }}
      >
        <SettingsIcon height={isLandingPage ? '18px' : '17px'} width={isLandingPage ? '17px' : '15px'} />
      </IconButton>
    </StyledCustomerActionRow>
  )
}

export const CustomerLogo = styled(Box)<CommonInterface>(({ isLandingPage, img, imageWidth, height, minWidth }) => ({
  borderRadius: '4px',
  padding: isLandingPage ? '3px 21px 3px 0px' : '0px 11px 0px 0px',
  marginRight: '16px',
  width: isLandingPage ? '90px' : imageWidth,
  height: isLandingPage ? '50px' : height ? height : '64px',
  backgroundImage: `url(${img})`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  minHeight: isLandingPage ? '50px' : height ? height : '64px',
  minWidth: isLandingPage ? '90px' : minWidth ? minWidth : imageWidth,
}))

export const ShadowedCard = styled(Card)<ShadowedCardProps>(
  ({ theme, changeColor, padding, isLandingPage = false, height, isAdminPortal, isProject }) => ({
    '&.MuiPaper-root': {
      backgroundColor: theme.palette.white[theme.palette.mode],
      color: changeColor ? theme.palette.white[theme.palette.mode] : theme.palette.primaryDark[theme.palette.mode],
      boxShadow: `${isLandingPage ? '0px 0px 6px' : isAdminPortal ? '0px' : '0px 2px 8px'} ${GetRGBA(
        theme.palette.black[theme.palette.mode],
        isLandingPage ? 0.29 : 0.4,
      )}`,
    },
    cursor: `${isLandingPage ? 'pointer' : 'default'}`,
    padding: padding,
    height: isLandingPage || isProject ? height ?? '56px' : '64px',
    width: isLandingPage ? 'auto' : '290px',
  }),
)

export const Chevron = styled(Box)(({ theme }) => ({
  padding: '0 8px 0 4px',
  alignContent: 'center',
  color: theme.palette.primaryMain[theme.palette.mode],
}))

const Name = styled(Typography)<CommonInterface>(({ isLandingPage }) => ({
  fontWeight: isLandingPage ? 'bold' : '600',
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: isLandingPage ? '21px' : '18px',
  color: '#050505',
  paddingRight: isLandingPage ? '19px' : '1px',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
}))

const StyledContainer = styled(Stack)<CommonInterface>(({ isLandingPage }) => ({
  display: 'flex',
  flexDirection: isLandingPage ? 'row' : 'column',
  alignItems: isLandingPage ? 'center' : 'start',
  justifyContent: 'space-between',
  margin: isLandingPage ? '14px 0px 13px 0px' : '6px 0px 8px 0px',
}))

export const StyledEmptyLogoBox = styled(Box)<CommonInterface>(({ isLandingPage, height, minWidth, fontSize }) => ({
  minHeight: isLandingPage ? '50px' : height ? height : '64px',
  minWidth: isLandingPage ? '90px' : minWidth ? minWidth : '71px',
  borderRadius: '4px',
  margin: isLandingPage ? '3px 21px 3px 0px' : '0px 7px 0px 0px',
  backgroundColor: '#bdbdbd',
  color: '#E9F0F8',
  textAlign: 'center',
  alignContent: 'center',
  fontSize: fontSize ?? '20px',
  fontWeight: '500',
}))

export const getNameLettersForLogo = name => {
  return name
    .split(' ')
    .slice(0, 2)
    .map(word => word.charAt(0))
    .join('').toUpperCase()
}

export const StyledCustomerActionRow = styled(Flex.Row)<CommonInterface>(
  ({ isLandingPage, theme, isServiceStationSmallHeader }) => ({
    alignContent: 'center',
    color: theme.palette.cadetBlue[theme.palette.mode],
    width: isLandingPage ? '44px' : '15px',
    height: isLandingPage ? '44px' : '17px',
    alignItems: 'center',
    justifyContent: 'center',
    border: isLandingPage ? '1px solid #C4C4C4' : 'none',
    borderRadius: '4px',
    marginLeft: isLandingPage ? '8px' : '12px',
    marginBottom: isServiceStationSmallHeader ? '2px' : '',
    marginRight: isServiceStationSmallHeader ? '8px' : '',
    '&:hover ': {
      borderColor: '#212121',
    },
    '&.Mui-focused ': {
      borderColor: '#050505',
    },
  }),
)

const CalendarButton = ({ customerId, name }: { customerId: number; name: string }) => {
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation(['calendar', 'common'])

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    const title = (
      <Box sx={{ width: '100%', textAlign: 'center' }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '18px' }}>
          {t('title', { ns: 'calendar' })}
        </Typography>
        {name && (
          <Typography component="span" sx={{ fontWeight: 500, fontSize: '18px', opacity: 0.7 }}>
            {` – ${name.trim()}`}
          </Typography>
        )}
      </Box>
    )
    showDialog(
      GlobalCalendarDialog,
      {
        componentProps: { customerId },
        opts: { maxWidth: 'xl', fullWidth: true },
        title: title as unknown as string,
      },
      undefined,
      false,
    )
  }

  return (
    <GridActionButton
      buttonIcon={<CalendarMonthOutlined sx={{ fontSize: 25 }} />}
      variant="outlined"
      shadowWeight={0}
      width="44px"
      height="44px"
      onClick={handleClick}
      toolTipTitle={t('title', { ns: 'calendar' })}
    />
  )
}

const VesselTasksButton = ({
  customerId,
  encryptedPermission,
}: {
  customerId: number
  encryptedPermission: string
}) => {
  const { showDialog } = useDialogCtx()
  const { t } = useTranslation('common')

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    showDialog(
      VesselTasksCalendarDialog,
      {
        componentProps: {
          customerId,
        },
        opts: {
          maxWidth: 'xl',
          fullWidth: true,
        },
        title: t('vessel-tasks', { ns: 'task' }),
      },
      undefined,
      false,
    )
  }

  return (
    <GridActionButton
      buttonIcon={<VesselIcon height="23px" width="22px" />}
      variant="outlined"
      shadowWeight={0}
      width="44px"
      height="44px"
      onClick={handleClick}
      toolTipTitle={t('vessel-tasks', { ns: 'task' })}
    />
  )
}
